import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getAllProvinces, getCitiesByProvinceCode, getCityInfoByCode } from '../../utils/cityUtils';

/**
 * 自定义城市选择组件
 * 支持省份和城市两级选择，存储城市代码
 */
const CitySelector = ({ value = '', onChange, isInvalid = false, size = 'medium' }) => {
	// 解析当前值（城市代码）
	const parseValue = (code) => {
		if (!code) return { provinceCode: '', cityCode: '' };

		const cityInfo = getCityInfoByCode(code);
		if (cityInfo) {
			return {
				provinceCode: cityInfo.provinceCode,
				cityCode: cityInfo.code,
			};
		}

		return { provinceCode: '', cityCode: '' };
	};

	const [selectedProvinceCode, setSelectedProvinceCode] = useState(() => parseValue(value).provinceCode);
	const [selectedCityCode, setSelectedCityCode] = useState(() => parseValue(value).cityCode);
	const prevValueRef = useRef(value);

	// 获取所有省份列表
	const provinces = useMemo(() => getAllProvinces(), []);

	// 获取当前省份的城市列表
	const availableCities = useMemo(() => {
		if (!selectedProvinceCode) return [];
		return getCitiesByProvinceCode(selectedProvinceCode);
	}, [selectedProvinceCode]);

	// 处理省份选择
	const handleProvinceChange = (e) => {
		const provinceCode = e.target.value;
		setSelectedProvinceCode(provinceCode);

		if (provinceCode) {
			const cities = getCitiesByProvinceCode(provinceCode);
			const defaultCityCode = cities.length > 0 ? cities[0].code : '';
			setSelectedCityCode(defaultCityCode);

			// 触发 onChange，传递城市代码
			if (onChange && defaultCityCode) {
				onChange({
					target: { value: defaultCityCode },
					provinceCode,
					cityCode: defaultCityCode,
				});
			}
		} else {
			setSelectedCityCode('');
			if (onChange) {
				onChange({
					target: { value: '' },
					provinceCode: '',
					cityCode: '',
				});
			}
		}
	};

	// 处理城市选择
	const handleCityChange = (e) => {
		const cityCode = e.target.value;
		setSelectedCityCode(cityCode);

		if (selectedProvinceCode && cityCode) {
			// 触发 onChange，传递城市代码
			if (onChange) {
				onChange({
					target: { value: cityCode },
					provinceCode: selectedProvinceCode,
					cityCode,
				});
			}
		}
	};

	// 同步外部 value 变化
	useEffect(() => {
		if (value !== prevValueRef.current) {
			prevValueRef.current = value;
			if (value) {
				const parsed = parseValue(value);
				setSelectedProvinceCode(parsed.provinceCode);
				setSelectedCityCode(parsed.cityCode);
			} else {
				setSelectedProvinceCode('');
				setSelectedCityCode('');
			}
		}
	}, [value]);

	const selectSize = size === 'small' ? 'sm' : size === 'large' ? 'lg' : '';

	return (
		<div className='d-flex gap-2 align-items-center'>
			<Form.Select value={selectedProvinceCode} onChange={handleProvinceChange} isInvalid={isInvalid && !selectedProvinceCode} size={selectSize} style={{ flex: 1 }}>
				<option value=''>选择省份</option>
				{provinces.map((province) => (
					<option key={province.code} value={province.code}>
						{province.name}
					</option>
				))}
			</Form.Select>
			<FontAwesomeIcon icon='arrow-right' className='text-muted' style={{ fontSize: '0.875rem' }} />
			<Form.Select
				value={selectedCityCode}
				onChange={handleCityChange}
				isInvalid={isInvalid && !selectedCityCode}
				size={selectSize}
				disabled={!selectedProvinceCode}
				style={{ flex: 1 }}
			>
				<option value=''>选择城市</option>
				{availableCities.map((city) => (
					<option key={city.code} value={city.code}>
						{city.name}
					</option>
				))}
			</Form.Select>
		</div>
	);
};

export default CitySelector;
